import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceStatus,
  UserImportDataSourceWorkdayReportApi,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { Label } from '../../../../../../components/CustomInputLabel';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import {
  FormRow,
  FormSection
} from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { Centered } from '../../../../../../layout/Centered';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc, updateDataInDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { useWorkdayConfig } from '../../../../../Org/pages/Integrations/integrations/workday/service';
import { CustomImportMappingForm2 } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.WORKDAY;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'firstName',
    otherKey: 'First_Name'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'Last_Name'
  },
  {
    dadoKey: 'email',
    otherKey: 'Work_Email'
  },
  {
    dadoKey: 'personalEmail',
    otherKey: 'Personal_Email'
  }
];

export const WorkdaySetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);
  const [config, loading] = useWorkdayConfig(orgId, userId);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [state, setState] = useState<Doc<UserImportDataSourceWorkdayReportApi>>(
    {
      id: shortid(),
      collection: FS.userImportDataSources,
      data: {
        orgId: orgId,
        type: UserImportSourceType.WORKDAY_REPORT_API,
        status: UserImportDataSourceStatus.PENDING,
        detailedStatus: '',
        lastUpdate: null,
        createdBy: userId,
        createdAt: now(),
        customMappings: mappings,
        dateFormat: 'YYYY-MM-DD', // kept for backward compatibility
        dateFormats: ['YYYY-MM-DD'], // default to ISO format
        topLevelItemName: 'Report_Entry',
        name: '',
        endpointId: '',
        dataShapeStyle: 'HRIS'
      }
    }
  );

  if (loading || !config) {
    return (
      <>
        <DialogContent>
          <FormRow>
            <Img maxHeight={32} src={META.logoUrl} />
          </FormRow>
          <Loader height={300} />
        </DialogContent>

        <DialogActions>
          <Button color="primary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="primary" disabled={true}>
            {t('Add source')}
          </Button>
        </DialogActions>
      </>
    );
  }

  if (!config.data.endpoints.length) {
    return (
      <>
        <DialogContent>
          <FormRow>
            <Img maxHeight={32} src={META.logoUrl} />
          </FormRow>
          <Centered height={300}>
            <Typography variant="caption">
              {t('No endpoints configured')}.
            </Typography>
          </Centered>
        </DialogContent>

        <DialogActions>
          <Button color="primary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="primary" disabled={true}>
            {t('Add source')}
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormSection>
          <Label
            label={t('API Endpoint')}
            subLabel={t(
              'Select from the endpoints set up on the Integrations page'
            )}
          />
          <Select
            value={state.data.endpointId}
            onChange={(ev) =>
              setState((prev) => {
                const endpoint = config.data.endpoints.find(
                  (e) => e.id === (ev.target.value as string)
                );
                if (endpoint) {
                  return updateDataInDoc(prev, {
                    endpointId: endpoint.id,
                    name: `Workday - ${endpoint.name}`
                  });
                }
                return prev;
              })
            }
            fullWidth
          >
            {config.data.endpoints.map((endpoint) => (
              <MenuItem key={endpoint.id} value={endpoint.id}>
                {endpoint.name}
              </MenuItem>
            ))}
          </Select>
        </FormSection>
        <FormSection>
          <Label label={t('Report Type')} />
          <Select
            value={state.data.dataShapeStyle}
            onChange={(ev) =>
              setState((prev) =>
                updateDataInDoc(prev, {
                  dataShapeStyle: ev.target.value as 'HRIS' | 'ATS'
                })
              )
            }
            fullWidth
          >
            <MenuItem key={'HRIS'} value={'HRIS'}>
              HRIS
            </MenuItem>
            <MenuItem key={'ATS'} value={'ATS'}>
              ATS
            </MenuItem>
          </Select>
        </FormSection>
        <FormSection>
          <DateFormatPicker
            value={state.data.dateFormats || [state.data.dateFormat]}
            onChange={(formats) =>
              setState((prev) =>
                updateDataInDoc(prev, {
                  dateFormats: formats,
                  dateFormat: formats[0] || 'YYYY-MM-DD' // keep legacy field in sync
                })
              )
            }
            subLabel={t(
              'Select the date formats that the Workday report may contain.'
            )}
          />
        </FormSection>
        <CustomImportMappingForm2
          additionalCompositeFields={['$FULL_NAME$', '$FULL_NAME_LN_FN$']}
          userFields={userFields}
          integrationName="Workday"
          name={t('Default Field Mapping')}
          mappings={mappings}
          onChange={(mappings) => setMappings(mappings)}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!state.data.endpointId}
          onClick={async () => {
            await setDoc(
              updateDataInDoc(state, {
                customMappings: mappings.filter(
                  (m) => !!m.otherKey && !!m.dadoKey
                )
              })
            );
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const WorkdayExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceWorkdayReportApi>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  // this is currently not in use anymore and needs to be cleaned up
  return null;
};

export const ImportSourceRowWorkday = ({
  d
}: {
  d: Doc<UserImportDataSourceWorkdayReportApi>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={d.data.name || META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
